import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { downloadCertificate } from "@/lib/certificate";
import {
  Loader2, CheckCircle2, Circle, PlayCircle, ArrowLeft, Clock, Award, ListChecks,
} from "lucide-react";

function QuizSection({ course, id, onDone }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(
    course.quiz_score != null ? { score: course.quiz_score, passed: course.quiz_passed } : null
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < course.quiz.length) {
      toast.error(t("courseDetail.quizAnswerAll"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${id}/quiz/submit`, { answers });
      setResult(data);
      if (data.passed) toast.success(t("courseDetail.quizPassedToast", { score: data.score }));
      else toast.error(t("courseDetail.quizFailedToast", { score: data.score }));
      onDone();
    } catch (e) {
      toast.error(t("courseDetail.quizSubmitError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-8 border-slate-200 p-6" data-testid="quiz-section">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="h-5 w-5 text-blue-600" />
        <h3 className="font-heading text-xl font-bold text-slate-900">{t("courseDetail.quizTitle")}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">{t("courseDetail.quizSubtitle")}</p>

      {result && (
        <div
          className={`mb-6 rounded-xl px-4 py-3 text-sm font-medium ${
            result.passed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}
          data-testid="quiz-result"
        >
          {result.passed ? t("courseDetail.quizPassed") : t("courseDetail.quizNotPassed")} — {t("courseDetail.quizYourScore")}: {result.score}%
        </div>
      )}

      <div className="space-y-6">
        {course.quiz.map((q, qi) => (
          <div key={q.question_id} data-testid="quiz-question">
            <p className="font-medium text-slate-800 mb-3">
              {qi + 1}. {q.text}
            </p>
            <div className="grid gap-2">
              {q.options.map((opt, oi) => {
                const chosen = answers[q.question_id] === oi;
                return (
                  <button
                    key={oi}
                    data-testid={`quiz-option-${qi}-${oi}`}
                    onClick={() => setAnswers({ ...answers, [q.question_id]: oi })}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                      chosen
                        ? "border-blue-500 bg-blue-50 text-blue-800"
                        : "border-slate-200 hover:bg-slate-50 text-slate-700"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border text-xs ${
                        chosen ? "border-blue-500 bg-blue-500 text-white" : "border-slate-300"
                      }`}
                    >
                      {String.fromCharCode(65 + oi)}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={submit}
        disabled={busy}
        className="mt-6 bg-blue-600 hover:bg-blue-700"
        data-testid="quiz-submit"
      >
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {result ? t("courseDetail.resubmitQuiz") : t("courseDetail.submitQuiz")}
      </Button>
    </Card>
  );
}

export default function CourseDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
    setCurrent((prev) => (prev ? data.lessons.find((l) => l.lesson_id === prev.lesson_id) : null) || data.lessons?.[0] || null);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const enroll = async () => {
    setBusy(true);
    try {
      await api.post(`/courses/${id}/enroll`);
      toast.success(t("courseDetail.enrolledSuccess"));
      await load();
    } catch (e) {
      toast.error(t("courseDetail.enrollError"));
    } finally {
      setBusy(false);
    }
  };

  const toggleLesson = async (lessonId) => {
    try {
      const { data } = await api.post(`/courses/${id}/lessons/${lessonId}/toggle`);
      setCourse((c) => ({
        ...c,
        completed_lessons: data.completed_lessons,
        progress: data.progress,
        certificate_eligible: data.progress === 100 && (!c.has_quiz || c.quiz_passed),
      }));
    } catch (e) {
      toast.error(t("courseDetail.enrollFirst"));
    }
  };

  const getCertificate = async () => {
    try {
      const { data } = await api.get(`/courses/${id}/certificate`);
      await downloadCertificate(data);
      toast.success(t("courseDetail.certSuccess"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("courseDetail.certNotEligible"));
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  const completed = course.completed_lessons || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="course-detail">
        <button
          onClick={() => navigate("/courses")}
          className="mb-6 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
          data-testid="back-to-courses"
        >
          <ArrowLeft className="h-4 w-4" /> {t("courseDetail.back")}
        </button>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div>
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900 shadow-lg">
              <img src={course.thumbnail} alt="" className="h-full w-full object-cover opacity-60" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <PlayCircle className="h-16 w-16 opacity-90" />
                <p className="mt-3 font-heading text-lg font-semibold px-6 text-center">
                  {current?.title || t("courseDetail.selectLesson")}
                </p>
                {current?.duration && <span className="mt-1 text-sm text-slate-300">{current.duration}</span>}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-600">{course.category}</Badge>
                <Badge variant="outline">{course.level}</Badge>
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-slate-900">{course.title}</h1>
              <p className="mt-1 text-sm text-slate-500">{t("courseDetail.by")} {course.instructor}</p>
              <p className="mt-4 text-slate-600 leading-relaxed">{course.description}</p>

              {current && course.enrolled && (
                <Button
                  data-testid="lesson-complete-toggle"
                  onClick={() => toggleLesson(current.lesson_id)}
                  variant={completed.includes(current.lesson_id) ? "outline" : "default"}
                  className={completed.includes(current.lesson_id) ? "mt-6" : "mt-6 bg-blue-600 hover:bg-blue-700"}
                >
                  {completed.includes(current.lesson_id) ? (
                    <><CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" /> {t("courseDetail.completed")}</>
                  ) : (
                    <>{t("courseDetail.markComplete")}</>
                  )}
                </Button>
              )}
            </div>

            {course.enrolled && course.has_quiz && (
              <QuizSection course={course} id={id} onDone={load} />
            )}
          </div>

          <aside className="lg:sticky lg:top-24 h-fit rounded-2xl border border-slate-200 bg-white p-6">
            {course.enrolled ? (
              <div className="mb-5">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-500">{t("courseDetail.yourProgress")}</span>
                  <span className="font-semibold text-slate-900" data-testid="course-progress">{course.progress}%</span>
                </div>
                <Progress value={course.progress} className="h-2" />
              </div>
            ) : (
              <Button
                data-testid="course-enroll-button"
                onClick={enroll}
                disabled={busy}
                className="mb-5 w-full bg-blue-600 hover:bg-blue-700"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("courseDetail.enrollFree")}
              </Button>
            )}

            {course.enrolled && (
              <div className="mb-5">
                {course.certificate_eligible ? (
                  <Button
                    onClick={getCertificate}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="download-certificate-button"
                  >
                    <Award className="mr-2 h-4 w-4" /> {t("courseDetail.downloadCertificate")}
                  </Button>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                    <Award className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
                    {t("courseDetail.certLocked")}{course.has_quiz ? t("courseDetail.certLockedQuiz") : ""}{t("courseDetail.certLockedTail")}
                  </div>
                )}
              </div>
            )}

            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
              {course.lessons.length} {t("courseDetail.lessonsCount")}
            </h4>
            <ul className="space-y-1" data-testid="lesson-list">
              {course.lessons.map((l, i) => {
                const done = completed.includes(l.lesson_id);
                const active = current?.lesson_id === l.lesson_id;
                return (
                  <li key={l.lesson_id}>
                    <button
                      data-testid="lesson-item"
                      onClick={() => setCurrent(l)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                        active ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                      ) : (
                        <Circle className="h-5 w-5 shrink-0 text-slate-300" />
                      )}
                      <span className="flex-1">
                        <span className={`block text-sm font-medium ${active ? "text-blue-700" : "text-slate-700"}`}>
                          {i + 1}. {l.title}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" /> {l.duration}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </main>
    </div>
  );
}
